/**
 * @fileoverview "+ Add substitute" — record a stand-in teacher.
 *
 * A substitute is a label, not an account. A name is all that is required:
 * they never sign in, and the admin who assigns them to a session writes that
 * session's notes. Email and phone are optional and exist only so the org can
 * reach them — filling one in still creates no login.
 *
 * Not offered on a private-lessons program: those have no class sessions for
 * anyone to stand in on.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Button } from "@/components/studio/primitives";

const inputClass =
  "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[10px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";

export default function AddSubstituteDialog({ open, onOpenChange, onAdded }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ name: "", email: "", phone: "", note: "" });
  }, [open]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Give the substitute a name");
      return;
    }
    setSaving(true);
    try {
      const res = await orgApi.post("/org/substitutes", {
        name: form.name.trim(),
        // Empty means absent, not "". A blank string would be stored and shown
        // as a contact that isn't one.
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        note: form.note.trim() || null,
      });
      toast.success(`${form.name.trim()} added as a substitute`);
      onAdded?.(res.data?.substitute);
      onOpenChange(false);
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't add that substitute"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl font-semibold">Add a substitute</DialogTitle>
        </DialogHeader>

        <form onSubmit={submit} data-testid="add-substitute-form">
          <input
            value={form.name}
            onChange={set("name")}
            placeholder="Name"
            aria-label="Substitute name"
            data-testid="substitute-name"
            className={inputClass}
          />
          <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">
            A name is all that&rsquo;s needed. A substitute doesn&rsquo;t sign in — you assign them to a session
            and write its notes.
          </p>

          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <input value={form.email} onChange={set("email")} type="email" placeholder="Email (optional)" data-testid="substitute-email" className={inputClass} />
            <input value={form.phone} onChange={set("phone")} placeholder="Phone (optional)" data-testid="substitute-phone" className={inputClass} />
          </div>
          <input
            value={form.note}
            onChange={set("note")}
            placeholder="Note (optional) — e.g. cello, weekday afternoons"
            data-testid="substitute-note"
            className={`${inputClass} mt-3`}
          />

          <div className="mt-5 flex items-center gap-3">
            <Button type="submit" disabled={saving} data-testid="substitute-submit">
              {saving ? "Adding…" : "Add substitute"}
            </Button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="font-sans text-[14px] text-[var(--text-tertiary)] hover:underline"
            >
              Cancel
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
